import React from 'react';
import { Resources } from '../../config/CommonResources';

// One tool call the agent made while producing a reply. Mirrors the backend AgentStepDto.
export interface IAgentTraceStep {
  id: string;
  tool: string;
  thought: string | null;
  // 'running', 'completed', or whatever raw state the agent reported (e.g. a failure).
  state: string;
  durationSeconds: number | null;
  dax: string[];
  datasetName: string | null;
}

const resourceText = (key: string): string | undefined => (Resources as Record<string, string>)[key];

// "GetSemanticModelSchema" -> "Get semantic model schema" - used for tools we have no friendly label for.
const humanizeTool = (tool: string): string => {
  const spaced = (tool || Resources.Agents_Trace_Working).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

export const doneLabel = (step: IAgentTraceStep): string => resourceText(`Agents_Trace_Tool_${step.tool}`) ?? humanizeTool(step.tool);
const runningLabel = (step: IAgentTraceStep): string => resourceText(`Agents_Trace_Running_${step.tool}`) ?? humanizeTool(step.tool);

const isFailed = (step: IAgentTraceStep): boolean => step.state !== 'running' && step.state !== 'completed';

export const formatDuration = (seconds: number | null): string => (seconds == null ? '' : `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)}s`);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={`agents-trace-chevron${open ? ' open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * Shown under an agent reply: a collapsed one-liner that expands to the tool calls behind that
 * answer, the agent's stated reasoning per step, the dataset used, and the DAX it ran.
 */
export const AgentTrace = React.memo(function AgentTrace({ steps }: { steps: IAgentTraceStep[] }): React.ReactElement | null {
  const [open, setOpen] = React.useState<boolean>(false);
  const [daxOpen, setDaxOpen] = React.useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = React.useState<string | null>(null);

  if (!steps.length) return null;

  const dataset = steps.find((s) => s.datasetName);
  const countLabel = `${steps.length} ${steps.length === 1 ? Resources.Agents_Trace_Step : Resources.Agents_Trace_Steps}`;

  const toggleDax = (stepId: string) => setDaxOpen((prev) => ({ ...prev, [stepId]: !prev[stepId] }));

  const copyDax = (key: string, dax: string) => {
    navigator.clipboard?.writeText(dax);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
  };

  return (
    <div className="agents-trace">
      <button
        type="button"
        className="agents-trace-toggle"
        aria-expanded={open}
        title={open ? Resources.Agents_Trace_HideSteps : Resources.Agents_Trace_ShowSteps}
        onClick={() => setOpen(!open)}
      >
        <Chevron open={open} />
        <span>{countLabel}</span>
        {dataset?.datasetName && <span className="agents-trace-dataset-name">· {dataset.datasetName}</span>}
      </button>
      {open && (
        <div className="agents-trace-body">
          {dataset?.datasetName && (
            <div className="agents-trace-dataset">
              <span className="agents-trace-dataset-label">{Resources.Agents_Trace_Dataset}</span>
              <b>{dataset.datasetName}</b>
            </div>
          )}
          <ol className="agents-trace-steps">
            {steps.map((step) => (
              <li key={step.id} className={`agents-trace-step${isFailed(step) ? ' failed' : ''}`}>
                <div className="agents-trace-step-head">
                  <span className="agents-trace-dot" aria-hidden="true" />
                  <span className="agents-trace-step-label">{doneLabel(step)}</span>
                  {step.durationSeconds != null && <span className="agents-trace-step-time">{formatDuration(step.durationSeconds)}</span>}
                  {step.dax.length > 0 && (
                    <button type="button" className="agents-trace-dax-toggle" aria-expanded={!!daxOpen[step.id]} onClick={() => toggleDax(step.id)}>
                      {daxOpen[step.id] ? Resources.Agents_Trace_HideDax : Resources.Agents_Trace_ViewDax}
                    </button>
                  )}
                </div>
                {/* The thought is model-generated text - always rendered as plain text, never as HTML. */}
                {step.thought && <div className="agents-trace-thought">{step.thought}</div>}
                {daxOpen[step.id] &&
                  step.dax.map((dax, index) => {
                    const key = `${step.id}:${index}`;
                    return (
                      <div key={key} className="agents-trace-dax">
                        <button type="button" className="agents-trace-dax-copy" onClick={() => copyDax(key, dax)}>
                          {copiedKey === key ? 'Copied' : Resources.Agents_Trace_CopyDax}
                        </button>
                        <pre>
                          <code>{dax}</code>
                        </pre>
                      </div>
                    );
                  })}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
});

/**
 * Shown while the agent is still working: finished steps as small chips, plus the current step
 * as the shimmering status line (falls back to "Thinking…" before the first step arrives).
 */
export function AgentLiveSteps({ steps, seconds }: { steps: IAgentTraceStep[]; seconds: number }): React.ReactElement {
  const current = [...steps].reverse().find((s) => s.state === 'running');
  const finished = steps.filter((s) => s.state !== 'running');
  return (
    <div className="agents-live">
      {finished.length > 0 && (
        <div className="agents-live-chips">
          {finished.map((step) => (
            <span key={step.id} className={`agents-live-chip${isFailed(step) ? ' failed' : ''}`}>
              {doneLabel(step)}
            </span>
          ))}
        </div>
      )}
      <div className="agents-chat-thinking">
        {current ? runningLabel(current) : 'Thinking'}… {seconds}s
      </div>
    </div>
  );
}
